import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, ShieldCheck, KeyRound } from "lucide-react";
import { RHEEL_LOGO_URL } from "@/constants/brand";

export default function Login() {
  const { user, login, mfaVerify } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // MFA step state
  const [mfaTicket, setMfaTicket] = useState(null);
  const [mfaEmail, setMfaEmail] = useState("");
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);
  const [useRecovery, setUseRecovery] = useState(false);
  const codeRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const err = new URLSearchParams(window.location.search).get("sso_error");
    if (err) {
      const map = {
        nouser: "No account here matches that Microsoft email. Ask your administrator to add you first.",
        tenant: "That Microsoft account isn't from an allowed organization.",
        denied: "Microsoft sign-in was cancelled or denied.",
        expired: "The sign-in request expired. Please try again.",
        invalid: "Microsoft sign-in failed. Please try again.",
        noemail: "That Microsoft account has no email address we can use.",
        not_configured: "Microsoft sign-in isn't set up yet.",
      };
      setError(map[err] || "Microsoft sign-in failed. Please try again.");
    }
  }, []);

  useEffect(() => {
    if (mfaTicket && codeRef.current) codeRef.current.focus();
  }, [mfaTicket]);

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (res.mfaRequired) {
      setMfaTicket(res.mfaTicket);
      setMfaEmail(res.email);
      setCode("");
      return;
    }
    navigate("/");
  };

  const onSubmitMfa = async (e) => {
    e.preventDefault();
    setError("");
    const cleaned = useRecovery ? code.trim().toLowerCase() : code.replace(/\D/g, "");
    if (!cleaned) { setError("Enter a code"); return; }
    setLoading(true);
    const res = await mfaVerify(mfaTicket, cleaned, trustDevice);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  const backToPassword = () => {
    setMfaTicket(null);
    setCode("");
    setError("");
    setUseRecovery(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF7F0] text-[#0F0F0F]">
      <div className="flex flex-col p-10 lg:p-16 border-r border-[#E5E0D5] relative">
        <div className="flex items-center gap-3 mb-16">
          <img src={RHEEL_LOGO_URL} alt="Rheel Group" className="h-14 w-auto object-contain" />
          <div className="border-l border-[#E5E0D5] pl-3">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#7A7A7A]">MSP Dashboard</div>
            <div className="font-mono text-xs text-[#1A1A1A]">v1.0</div>
          </div>
        </div>

        <div className="my-auto max-w-md w-full">
          {!mfaTicket ? (
            <>
              <div className="uppercase-label mb-4">[01] Authenticate</div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight leading-tight mb-2">
                Sign in to control inventory.
              </h2>
              <p className="text-sm text-[#5A5A5A] mb-10">
                Track laptops, monitors, phones and every device that moves through your org — with an audit trail you can defend.
              </p>

              {error && (
                <div data-testid="login-error" className="mb-6 border border-[#B91C1C]/40 bg-[rgba(185,28,28,0.08)] text-[#B91C1C] px-3 py-2 text-xs font-mono">
                  {error}
                </div>
              )}

              {!showPassword ? (
                <div className="space-y-5">
                  <Button
                    type="button"
                    data-testid="microsoft-signin-btn"
                    onClick={() => {
                      const url = `${BACKEND}/api/auth/microsoft/login`;
                      // Microsoft refuses to render its login inside an iframe (e.g. the preview pane).
                      // Break out to the top window; if that is blocked, open a new tab.
                      const embedded = window.top !== window.self;
                      if (!embedded) {
                        window.location.href = url;
                        return;
                      }
                      try {
                        window.top.location.href = url;
                      } catch (e) {
                        window.open(url, "_blank", "noopener");
                      }
                    }}
                    className="w-full rounded-none bg-[#2F2F2F] hover:bg-[#1A1A1A] text-white font-mono text-xs tracking-[0.2em] uppercase h-12 flex items-center justify-center gap-3"
                  >
                    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                    </svg>
                    Sign in with Microsoft
                  </Button>
                  <p className="text-xs font-mono text-[#7A7A7A]">
                    Use your organization&apos;s Microsoft 365 account. Need access? <span className="text-[#3A3A3A]">Contact your administrator.</span>
                  </p>
                  <button
                    type="button"
                    data-testid="admin-signin-toggle"
                    onClick={() => { setShowPassword(true); setError(""); }}
                    className="text-[11px] font-mono text-[#9A9A9A] hover:text-[#5A5A5A] underline decoration-dotted underline-offset-4"
                  >
                    Administrator sign-in
                  </button>
                </div>
              ) : (
              <form onSubmit={onSubmitPassword} className="space-y-5" data-testid="login-form">
                <div className="uppercase-label text-[#B45309] -mt-2 mb-1">Break-glass admin access</div>
                <div>
                  <Label className="uppercase-label">Email</Label>
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2 rounded-none bg-[#FAF7F0] border-[#D6CFC1] focus-visible:ring-1 focus-visible:ring-[#F37B2A] focus-visible:ring-offset-0 font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="uppercase-label">Password</Label>
                  <Input
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-2 rounded-none bg-[#FAF7F0] border-[#D6CFC1] focus-visible:ring-1 focus-visible:ring-[#F37B2A] focus-visible:ring-offset-0 font-mono text-sm"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  disabled={loading}
                  className="w-full rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-[0.2em] uppercase h-11"
                >
                  {loading ? "Authenticating…" : "Sign in"}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <button
                  type="button"
                  data-testid="back-to-sso-btn"
                  onClick={() => { setShowPassword(false); setError(""); }}
                  className="text-xs font-mono text-[#5A5A5A] hover:text-[#0F0F0F]"
                >
                  ← Back to Microsoft sign-in
                </button>
              </form>
              )}
            </>
          ) : (
            <>
              <div className="uppercase-label mb-4">[02] Two-step Verification</div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight leading-tight mb-2">
                Authenticator code.
              </h2>
              <p className="text-sm text-[#5A5A5A] mb-2">
                {useRecovery
                  ? "Enter one of your one-time recovery codes."
                  : <>Open your authenticator app and enter the 6-digit code for <span className="font-mono text-[#3A3A3A]">{mfaEmail}</span>.</>
                }
              </p>
              <p className="text-xs font-mono text-[#9A9A9A] mb-8 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3" /> Codes refresh every 30 seconds.
              </p>

              <form onSubmit={onSubmitMfa} className="space-y-5" data-testid="mfa-form">
                <div>
                  <Label className="uppercase-label">
                    {useRecovery ? "Recovery Code" : "6-digit Code"}
                  </Label>
                  <Input
                    ref={codeRef}
                    data-testid={useRecovery ? "mfa-recovery-input" : "mfa-code-input"}
                    type="text"
                    inputMode={useRecovery ? "text" : "numeric"}
                    autoComplete={useRecovery ? "off" : "one-time-code"}
                    value={code}
                    onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder={useRecovery ? "xxxx-xxxx-xxxx" : "000000"}
                    required
                    className="mt-2 rounded-none bg-[#FAF7F0] border-[#D6CFC1] focus-visible:ring-1 focus-visible:ring-[#F37B2A] focus-visible:ring-offset-0 font-mono text-lg tracking-[0.3em] text-center"
                    maxLength={useRecovery ? 14 : 6}
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="trust-device-label">
                  <Checkbox
                    data-testid="trust-device-checkbox"
                    checked={trustDevice}
                    onCheckedChange={(v) => setTrustDevice(!!v)}
                    className="rounded-none border-[#D6CFC1] data-[state=checked]:bg-[#F37B2A]"
                  />
                  <span className="font-mono text-xs text-[#3A3A3A]">Trust this device for 30 days</span>
                </label>

                {error && (
                  <div data-testid="mfa-error" className="border border-[#B91C1C]/40 bg-[rgba(185,28,28,0.08)] text-[#B91C1C] px-3 py-2 text-xs font-mono">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="mfa-submit-btn"
                  disabled={loading}
                  className="w-full rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-[0.2em] uppercase h-11"
                >
                  {loading ? "Verifying…" : "Verify"}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }}
                    data-testid="toggle-recovery-btn"
                    className="text-xs font-mono text-[#5A5A5A] hover:text-[#0F0F0F] inline-flex items-center gap-1.5"
                  >
                    <KeyRound className="w-3 h-3" /> {useRecovery ? "Use authenticator app" : "Use a recovery code"}
                  </button>
                  <button
                    type="button"
                    onClick={backToPassword}
                    data-testid="back-to-password-btn"
                    className="text-xs font-mono text-[#5A5A5A] hover:text-[#0F0F0F]"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        <div className="mt-auto pt-10 grid grid-cols-3 gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A]">
          <div>SECURE · JWT</div>
          <div>SOC-READY</div>
          <div>v1.0_register</div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden bg-white">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1496181133206-80ce9b88a853?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(40%) contrast(1.05)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="uppercase-label mb-3">Control Room</div>
          <div className="font-mono text-sm text-[#3A3A3A] max-w-md leading-relaxed">
            Every laptop, monitor, phone & server in one structured registry — searchable, exportable, fully audited.
          </div>
        </div>
      </div>
    </div>
  );
}
